/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-13 17:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import classnames from 'classnames';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { RangeSlider, Icon, TextStyle } from '@shopify/polaris';
import { CaretDownMinor } from '@shopify/polaris-icons';
import { Field } from 'redux-form';
import { components } from 'react-select';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import RenderColorPickerItem from 'src/components/views/common/render-color-picker-item';
import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';

import ShopifyFonts from 'src/constants/shopify-fonts.json';
import { normalizeNumberRangeSlider } from 'src/helpers/form-validation-helper';
import { minValue } from 'src/helpers/form-validation-helper';
import { DropdownIndicator, styles } from './react-select-style';
import { DEFAULT_FONT_SIZE } from 'src/constants/theme-settings';
import { TEXT_TRANSFORM_OPTIONS, MIN_FONT_SIZE, MAX_FONT_SIZE } from 'src/constants/theme-settings';
import { TEXT_ALIGNMENT_OPTIONS } from 'src/constants/theme-settings.jsx';

type Props = {
  formValues: any,
  prefix: string,
  labelColor: string,
  updateField: (field: string, value: any) => void,
  titleSmall: boolean,
  disabledAll: boolean,
  showColor: boolean,
  showTextAlignment: boolean,
  showTextTransform: boolean,
  maxFontSize: number,
  minFontSize: number,
  customFieldNameColor?: string,
  customFieldNameSize?: string,
  customFieldNameStyle?: string,
  customFieldNameFamily?: string,
  customFieldNameAlignment?: string,
  customFieldNameTransform?: string
};

type ComposedProps = Props & WithI18nProps;

const getValidateMinFontSize = minFontSize => minValue(minFontSize ?? MIN_FONT_SIZE);

class TextForm extends Component<ComposedProps> {
  validateMinFontSize = getValidateMinFontSize(this.props.minFontSize);

  componentWillReceiveProps(nextProps) {
    const { formValues } = this.props;
    const {
      formValues: nextFormValues,
      updateField,
      prefix,
      customFieldNameStyle,
      customFieldNameFamily
    } = nextProps;
    const fontFamily = _.get(formValues, customFieldNameFamily || `${prefix}.fontFamily`, '');
    const nextFontFamily = _.get(
      nextFormValues,
      customFieldNameFamily || `${prefix}.fontFamily`,
      ''
    );
    if (fontFamily !== nextFontFamily) {
      const font = ShopifyFonts.find(font => font?.variants[0]?.family === nextFontFamily);
      const value = `${font?.variants[0]?.style}-${font?.variants[0]?.weight}`;
      updateField(customFieldNameStyle || `${prefix}.fontStyle`, value);
      // this.handleImportFont(font?.variants[0]?.url || '')
    }
  }

  handleChangeFontStyle = fontStyle => {
    const { formValues, prefix, customFieldNameFamily } = this.props;
    const [style, weight] = fontStyle.split('-');

    const fontFamily = _.get(formValues, customFieldNameFamily || `${prefix}.fontFamily`, '');
    const font = ShopifyFonts.find(font => font?.variants[0]?.family === fontFamily);
    const variant = (font?.variants || []).find(
      item => item.style === style && item.weight === weight
    );

    this.handleImportFont(variant?.url || '');
  };

  // Handle blur input
  onBlurTextField = (event, value, name) => {
    const { updateField } = this.props;
    if (!/^\d+$/.test(value)) {
      event.preventDefault();
      updateField(name, DEFAULT_FONT_SIZE);
    }
  };

  render() {
    const {
      i18n,
      formValues,
      prefix,
      labelColor,
      updateField,
      titleSmall,
      disabledAll,
      showColor,
      showTextAlignment,
      showTextTransform,
      maxFontSize,
      minFontSize,
      customFieldNameColor,
      customFieldNameSize,
      customFieldNameStyle,
      customFieldNameFamily,
      customFieldNameAlignment,
      customFieldNameTransform
    } = this.props;
    const formattedFonts = ShopifyFonts.map(item => {
      return {
        ...item,
        label: item.name,
        value: item?.variants[0]?.family || item.default_variant_handle
      };
    });

    const backgroundColor = _.get(formValues, customFieldNameColor || `${prefix}.color`, '');
    const fontFamily = _.get(formValues, customFieldNameFamily || `${prefix}.fontFamily`, {});
    const fontSize = _.get(
      formValues,
      customFieldNameSize || `${prefix}.fontSize`,
      DEFAULT_FONT_SIZE
    );
    const font = ShopifyFonts.find(font => font?.variants[0]?.family === fontFamily);

    const formattedVariant = (font?.variants || []).map(item => {
      const value = `${item?.style}-${item?.weight}`;
      return {
        ...item,
        label: item.name,
        value: value
      };
    });

    const subTitleClass = classnames('subtitle', {
      disabled: disabledAll
    });

    return (
      <>
        {showColor && (
          <div className="form-section">
            <p className="title title--small">
              {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
            </p>
            <RenderColorPickerItem
              colorValue={backgroundColor}
              fieldName={customFieldNameColor || `${prefix}.color`}
              fieldLabel={labelColor}
              updateField={updateField}
              disabled={disabledAll}
            />
          </div>
        )}

        {/* FONT SECTION */}
        <div className="form-section" style={{ marginTop: '1rem' }}>
          <p className={`title ${titleSmall ? 'title--small' : ''}`}>
            {i18n.translate('AdminHTML.ThemeSetting.Form.font_Title')}
          </p>
          <div className="form-section__item">
            <Field
              name={customFieldNameFamily || `${prefix}.fontFamily`}
              component={RenderSelect2}
              options={formattedFonts}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.fontFamily_Title')}
              components={{ DropdownIndicator: CareIndicator }}
              styles={styles}
              isDisabled={disabledAll}
              placeholder=""
            />

            <div style={{ marginTop: '8px' }}>
              <TextStyle variation="subdued">
                {i18n.translate('AdminHTML.ThemeSetting.Form.fontFamily_Note1')}
              </TextStyle>
              <br />
              <TextStyle variation="subdued">
                {i18n.translate('AdminHTML.ThemeSetting.Form.fontFamily_Note2')}
              </TextStyle>
            </div>
          </div>

          <div className="form-section__item">
            <Field
              name={customFieldNameStyle || `${prefix}.fontStyle`}
              component={RenderSelect2}
              options={formattedVariant}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.fontStyle_Title')}
              components={{ DropdownIndicator }}
              styles={styles}
              isSearchable={false}
              isDisabled={disabledAll}
              placeholderEmpty=""
              // onChange={this.handleChangeFontStyle}
            />
          </div>

          <div className="form-section__item form-section__item--slider">
            <Field
              name={customFieldNameSize || `${prefix}.fontSize`}
              component={RenderTextField}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.fontSize_Title')}
              onBlur={(event, value) =>
                this.onBlurTextField(event, value, customFieldNameSize || `${prefix}.fontSize`)
              }
              normalize={(value, prevValue) =>
                normalizeNumberRangeSlider(value, prevValue, maxFontSize ?? MAX_FONT_SIZE)
              }
              validate={[this.validateMinFontSize]}
              anotherProps={{
                disabled: disabledAll,
                connectedLeft: (
                  <RangeSlider
                    value={fontSize}
                    onChange={value =>
                      updateField(customFieldNameSize || `${prefix}.fontSize`, value)
                    }
                    disabled={disabledAll}
                    max={maxFontSize ?? MAX_FONT_SIZE}
                    min={minFontSize ?? MIN_FONT_SIZE}
                  />
                ),
                connectedRight: (
                  <div className="text-field-unit">
                    <span>px</span>
                  </div>
                )
              }}
            />
          </div>
        </div>

        {/* TEXT SECTION */}
        {(showTextAlignment || showTextTransform) && (
          <div className="form-section" style={{ marginTop: '12px' }}>
            <p className="title title--small">
              {i18n.translate('AdminHTML.ThemeSetting.Form.text_Title')}
            </p>
            {showTextAlignment && (
              <div className="form-section__item">
                <p className={subTitleClass}>
                  {i18n.translate('AdminHTML.ThemeSetting.Form.textAlignment_Title')}
                </p>
                <div className="radio-button-wrapper">
                  {TEXT_ALIGNMENT_OPTIONS.map((item, index) => (
                    <Field
                      key={index}
                      name={customFieldNameAlignment || `${prefix}.textAlignment`}
                      id={item.value}
                      component={RenderRadioButton2}
                      label={item.label}
                    />
                  ))}
                </div>
              </div>
            )}
            {showTextTransform && (
              <div className="form-section__item">
                <p className={subTitleClass}>
                  {i18n.translate('AdminHTML.ThemeSetting.Form.textTransform_Title')}
                </p>
                <div className="radio-button-wrapper">
                  {TEXT_TRANSFORM_OPTIONS.map((item, index) => (
                    <Field
                      key={index}
                      name={customFieldNameTransform || `${prefix}.textTransform`}
                      id={item.value}
                      component={RenderRadioButton2}
                      label={item.label}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  }
}

export default withI18n()(TextForm);

const CareIndicator = props => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <Icon source={CaretDownMinor} color="base" />
      </components.DropdownIndicator>
    )
  );
};
